import { useQuery } from '@tanstack/react-query';
import { ListTenantsResponse, listTenantsContract } from '@bliks-io/contracts';
import { apiClient } from '@/api/client';

/**
 * Query key factory for tenant-related queries
 */
export const tenantKeys = {
  all: ['tenants'] as const,
  lists: () => [...tenantKeys.all, 'list'] as const,
  list: () => [...tenantKeys.lists()] as const,
  users: (tenantId: string) => [...tenantKeys.all, tenantId, 'users'] as const,
  /** Mutation key prefix shared by the tenant-user row actions. */
  userRowMutations: (tenantId: string) =>
    [...tenantKeys.all, tenantId, 'user-row-mutation'] as const,
  sendUserInvitationMutation: (tenantId: string) =>
    [...tenantKeys.userRowMutations(tenantId), 'send-invitation'] as const,
  generateUserActivationLinkMutation: (tenantId: string) =>
    [...tenantKeys.userRowMutations(tenantId), 'activation-link'] as const
};

/**
 * List tenants query
 *
 * Fetches all tenants in the system (super-admin only).
 * Returns an array of tenants with their id, name, slug, status, and type.
 *
 * @example
 * ```tsx
 * function TenantListPage() {
 *   const { data, isLoading, error } = useListTenants();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error loading tenants</div>;
 *
 *   return (
 *     <ul>
 *       {data?.tenants.map(tenant => (
 *         <li key={tenant._id}>{tenant.name}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useListTenants() {
  return useQuery({
    queryKey: tenantKeys.list(),
    queryFn: async ({ signal }): Promise<ListTenantsResponse> => {
      const { data } = await apiClient.get<ListTenantsResponse>(listTenantsContract.path, {
        signal
      });
      return data;
    }
  });
}
